// React hook wrapping the shared cartEngine with localStorage persistence.
// Usable by both the customer storefront and the POS (pass a distinct storageKey).
import { useState, useEffect, useCallback } from 'react';
import * as engine from './cartEngine';

export function useCart(storageKey = 'sspice_cart') {
  const [cart, setCart] = useState(() => {
    try {
      const saved = localStorage.getItem(storageKey);
      return saved ? JSON.parse(saved) : [];
    } catch {
      return [];
    }
  });

  useEffect(() => {
    try {
      localStorage.setItem(storageKey, JSON.stringify(cart));
    } catch {
      /* ignore quota errors */
    }
  }, [cart, storageKey]);

  const add = useCallback((menuItem, opts) => {
    setCart((c) => engine.addItem(c, engine.createCartItem(menuItem, opts)));
  }, []);

  const setQty = useCallback((signature, qty) => {
    setCart((c) => engine.updateQty(c, signature, qty));
  }, []);

  const remove = useCallback((signature) => {
    setCart((c) => engine.removeItem(c, signature));
  }, []);

  const clear = useCallback(() => setCart([]), []);

  // Bulk-replace the entire cart in one shot — used by Hold/Resume, where
  // a previously-saved cart snapshot needs to come back exactly as it was,
  // not rebuilt item-by-item through add() (which expects raw menu items,
  // not already-serialized cart entries).
  const replaceCart = useCallback((newCart) => setCart(newCart || []), []);

  const totals = useCallback((opts) => engine.computeTotals(cart, opts), [cart]);

  return { cart, add, setQty, remove, clear, replaceCart, totals, count: engine.itemCount(cart) };
}
